// Question 2: are you building what they asked for? Two facts per capability:
// in the original idea (the comprises edge, AI-proposed and confirmed here) and
// asked for by customers (a mitigates/enables edge onto a need, deterministic).
// Pending proposals sit in a Suggested block; confirmed capabilities fall into
// four groups, of which the two off-diagonal ones carry the signal.

import { useState } from 'react';
import { Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { SectionLabel } from '@/components/ui/section-label';
import { cn } from '@/lib/utils';
import { NODE_LABEL } from '@/lib/node-visuals';
import { askedForLine, groupCapabilities, pendingMappings } from '@/lib/alignment-groups';
import { useApplyAlignment, useRemoveComprises, useSetComprises } from '@/hooks/use-alignment';
import { useUIStore } from '@/stores';
import { JOURNEY_STEPS } from '@/features/project/journey-steps';
import { toast } from '@/hooks/use-toast';
import type { AlignmentCapability, AlignmentProposalRow, AlignmentReport, MappingProposal } from '@/types/api';

export function MappingSection({
  projectId,
  ideaNodeId,
  report,
  row,
  canEdit,
}: {
  projectId: string;
  ideaNodeId: string | undefined;
  report: AlignmentReport;
  row: AlignmentProposalRow | null | undefined;
  canEdit: boolean;
}) {
  const apply = useApplyAlignment(projectId);
  const setComprises = useSetComprises(projectId);
  const removeComprises = useRemoveComprises(projectId);
  const setViewMode = useUIStore((s) => s.setViewMode);

  // The PM's current choice per pending row, keyed by proposal id; a row with
  // no entry shows the AI's read. Lifted here so Confirm all can submit each
  // row's current value.
  const [choices, setChoices] = useState<Record<string, boolean>>({});
  // Open by default here: the reason is what says which words in the idea
  // the AI matched (or failed to match), which is the whole basis of the call.
  const [showReasons, setShowReasons] = useState(true);

  // A capability deleted since the run is not actionable, so it is not shown.
  const known = new Set(report.capabilities.map((c) => c.nodeId));
  const pending = pendingMappings(row).filter((p) => known.has(p.capabilityId));
  const pendingIds = new Set(pending.map((p) => p.capabilityId));
  const groups = groupCapabilities(report.capabilities, pendingIds);
  const busy = apply.isPending || setComprises.isPending || removeComprises.isPending;
  const choiceFor = (p: MappingProposal) => choices[p.id] ?? p.inIdea;

  const confirm = async (p: MappingProposal) => {
    try {
      await apply.mutateAsync({ proposalId: p.id, inIdea: choiceFor(p) });
    } catch (err) {
      toast({ title: "Couldn't confirm the mapping", description: err instanceof Error ? err.message : '', variant: 'destructive' });
    }
  };
  // One apply per row, in order; no batch endpoint.
  const confirmAll = async () => {
    for (const p of pending) await confirm(p);
  };
  const markInIdea = async (cap: AlignmentCapability) => {
    if (!ideaNodeId) return;
    try {
      await setComprises.mutateAsync({ ideaNodeId, capabilityId: cap.nodeId });
    } catch (err) {
      toast({ title: "Couldn't update the mapping", description: err instanceof Error ? err.message : '', variant: 'destructive' });
    }
  };
  const markDiscovery = async (cap: AlignmentCapability) => {
    if (!cap.comprisesEdgeId) return;
    try {
      await removeComprises.mutateAsync({ edgeId: cap.comprisesEdgeId });
    } catch (err) {
      toast({ title: "Couldn't update the mapping", description: err instanceof Error ? err.message : '', variant: 'destructive' });
    }
  };

  const hasRun = !!row;
  const anyMapped = report.capabilities.some((c) => c.inIdea);

  return (
    <section className="rounded-lg border bg-card p-5 space-y-4">
      <h2 className="font-semibold flex items-center gap-2">
        <Lightbulb className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <span className="text-muted-foreground">2.</span>
        Are you building what they asked for?
      </h2>

      {report.capabilities.length === 0 ? (
        <p className="text-sm text-muted-foreground">No capabilities yet. Decide what to build in step 6.</p>
      ) : (
        <>
          {pending.length > 0 && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <SectionLabel>Suggested</SectionLabel>
                  {/* The per-row reasons name the words in the idea that did or did not
                      match, so the idea itself is a link rather than quoted here. */}
                  <p className="text-sm text-muted-foreground">
                    The AI compared each capability's title and description with{' '}
                    <button
                      type="button"
                      className="underline underline-offset-2 hover:text-foreground"
                      onClick={() => setViewMode(JOURNEY_STEPS.idea.view)}
                    >
                      your idea as you wrote it in step 1
                    </button>
                    . Confirm, or switch it.
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {pending.some((p) => p.rationale) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-muted-foreground"
                      aria-expanded={showReasons}
                      onClick={() => setShowReasons((v) => !v)}
                    >
                      {showReasons ? 'Hide reasons' : 'Show reasons'}
                    </Button>
                  )}
                  {canEdit && (
                    <Button size="sm" variant="outline" className="h-7" disabled={busy} onClick={confirmAll}>
                      Confirm all
                    </Button>
                  )}
                </div>
              </div>
              <ul className="space-y-2">
                {pending.map((p) => (
                  <li key={p.id} className="rounded-md border p-3 text-sm">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <p className="leading-snug">
                          <Badge tone="capability" className="mr-2 align-middle">
                            {NODE_LABEL.capability}
                          </Badge>
                          <span className="align-middle">{p.capabilityTitle || '(untitled)'}</span>
                        </p>
                        <OriginToggle
                          value={choiceFor(p)}
                          disabled={!canEdit || busy}
                          onChange={(v) => setChoices((c) => ({ ...c, [p.id]: v }))}
                        />
                        {showReasons && p.rationale && <p className="text-sm text-foreground">{p.rationale}</p>}
                      </div>
                      {canEdit && (
                        <Button size="sm" variant="outline" className="h-7 shrink-0" disabled={busy} onClick={() => confirm(p)}>
                          Confirm
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!hasRun && !anyMapped ? (
            <p className="text-sm text-muted-foreground">
              Press &ldquo;Weigh the evidence&rdquo; above to sort your capabilities by where they came from.
            </p>
          ) : (
            <div className="space-y-4">
              <CapabilityGroup title="Planned, and customers asked for it" tone="success" caps={groups.core} canEdit={canEdit} busy={busy} onMarkInIdea={markInIdea} onMarkDiscovery={markDiscovery} />
              <CapabilityGroup
                title="Planned, but nobody asked for it"
                line="Capabilities in your idea that no customer need calls for. The clearest sign you're building for yourself."
                tone="warning"
                caps={groups.plannedUnasked}
                canEdit={canEdit}
                busy={busy}
                onMarkInIdea={markInIdea}
                onMarkDiscovery={markDiscovery}
              />
              <CapabilityGroup
                title="Customers asked for it, but it wasn't in your idea"
                line="Discovery found these. Decide whether they belong in the idea."
                tone="info"
                caps={groups.askedUnplanned}
                canEdit={canEdit}
                busy={busy}
                onMarkInIdea={markInIdea}
                onMarkDiscovery={markDiscovery}
              />
              <CapabilityGroup
                title="Neither planned nor asked for"
                line="Nothing points at these. Consider dropping them."
                tone="neutral"
                caps={groups.orphan}
                canEdit={canEdit}
                busy={busy}
                onMarkInIdea={markInIdea}
                onMarkDiscovery={markDiscovery}
              />
            </div>
          )}
        </>
      )}
    </section>
  );
}

// Two-option control pre-set to the AI's read. Buttons rather than a select so
// both options are visible and the row stays one line.
function OriginToggle({ value, disabled, onChange }: { value: boolean; disabled: boolean; onChange: (v: boolean) => void }) {
  const opt = (v: boolean, label: string) => (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={value === v}
      onClick={() => onChange(v)}
      className={cn('px-2 py-1 text-xs', value === v ? 'bg-accent font-medium' : 'text-muted-foreground hover:bg-accent/50')}
    >
      {label}
    </button>
  );
  return (
    <div className="inline-flex overflow-hidden rounded-md border">
      {opt(true, 'In your idea')}
      {opt(false, 'From discovery')}
    </div>
  );
}

function CapabilityGroup({
  title,
  line,
  tone,
  caps,
  canEdit,
  busy,
  onMarkInIdea,
  onMarkDiscovery,
}: {
  title: string;
  line?: string;
  tone: BadgeTone;
  caps: AlignmentCapability[];
  canEdit: boolean;
  busy: boolean;
  onMarkInIdea: (cap: AlignmentCapability) => void;
  onMarkDiscovery: (cap: AlignmentCapability) => void;
}) {
  if (caps.length === 0) return null;
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-medium flex items-center gap-2">
          <Badge tone={tone}>{caps.length}</Badge>
          {title}
        </h3>
      </div>
      {line && <p className="text-xs text-muted-foreground">{line}</p>}
      <ul className="space-y-1.5">
        {caps.map((cap) => (
          <li key={cap.nodeId} className="rounded-md border p-3 text-sm flex items-start justify-between gap-4">
            <p className="min-w-0 flex-1 leading-snug">
              <Badge tone="capability" className="mr-2 align-middle">
                {NODE_LABEL.capability}
              </Badge>
              <span className="align-middle">{cap.title || '(untitled)'}</span>
              <span className="ml-2 text-xs text-muted-foreground align-middle">{askedForLine(cap)}</span>
            </p>
            {canEdit && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 shrink-0 text-xs text-muted-foreground"
                disabled={busy || (cap.inIdea && !cap.comprisesEdgeId)}
                onClick={() => (cap.inIdea ? onMarkDiscovery(cap) : onMarkInIdea(cap))}
              >
                {cap.inIdea ? 'Not in my idea after all' : 'It was in my idea'}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
